import { parseKrPaymentReturnTarget, type KrPaymentReturnTarget } from '@shared/config/auth/kr-payment-return'
import { buildAuthSessionRecoveryUrl } from '@shared/routes/builders/auth-session-recovery-url'
import { buildAuthSessionResetUrl } from '@shared/routes/builders/auth-session-reset-url'

let activePaymentReturn: KrPaymentReturnTarget | null = null

/** Desktop SDK approvals live only in memory until the same-order confirmation finishes. */
export async function withKrPaymentAuthReturn<T>(target: KrPaymentReturnTarget, operation: () => Promise<T>) {
  if (activePaymentReturn) {
    throw new Error('KR_PAYMENT_RETURN_ALREADY_ACTIVE')
  }
  activePaymentReturn = target
  try {
    return await operation()
  } finally {
    activePaymentReturn = null
  }
}

/** Keep provider data out of the login URL and its Redis destination record. */
export async function navigateToPaymentAwareAuthRecovery(reset = false): Promise<void> {
  const current = new URL(window.location.href || '/', window.location.origin)
  const target = activePaymentReturn ?? parseKrPaymentReturnTarget(current.pathname, current.searchParams)
  const build = reset ? buildAuthSessionResetUrl : buildAuthSessionRecoveryUrl
  if (!target) {
    window.location.replace(build())
    return
  }
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 10_000)
  try {
    const response = await fetch(new URL('/api/v2/authentication/kr-payment-return', current.origin), {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(target),
      signal: controller.signal,
    })
    const result: unknown = await response.json()
    if (
      !response.ok ||
      !result ||
      typeof result !== 'object' ||
      !('context' in result) ||
      typeof result.context !== 'string' ||
      !result.context ||
      result.context.length > 20_000
    ) {
      throw new Error('KR_PAYMENT_RETURN_UNAVAILABLE')
    }
    window.location.replace(
      build({ destination: 'KR_PAYMENT_RETURN', additionalParams: { paymentReturnContext: result.context } }),
    )
  } finally {
    clearTimeout(timeout)
  }
}
